
import localStorageService from './localStorageService'
import toast from 'react-hot-toast'

/**
 * Sync Service for managing synchronization between local IndexedDB and cloud storage
 */
class SyncService {
  constructor() {
    this.syncQueue = []
    this.isSyncing = false
    this.syncConflicts = []
    this.lastSyncTime = null
    this.conflictResolutionStrategy = 'manual' // 'manual', 'local_wins', 'cloud_wins', 'merge'
  }

  /**
   * Initialize sync service with cloud storage context
   */
  initialize(cloudStorageContext) {
    this.cloudStorage = cloudStorageContext
    this.loadSyncState()
  }

  /**
   * Load sync state from localStorage
   */
  loadSyncState() {
    try {
      const syncState = localStorage.getItem('syncState')
      if (syncState) {
        const state = JSON.parse(syncState)
        this.lastSyncTime = state.lastSyncTime ? new Date(state.lastSyncTime) : null
        this.conflictResolutionStrategy = state.conflictResolutionStrategy || 'manual'
      }
    } catch (error) {
      console.error('Failed to load sync state:', error)
    }
  }

  /**
   * Save sync state to localStorage
   */
  saveSyncState() {
    try {
      const syncState = {
        lastSyncTime: this.lastSyncTime?.toISOString(),
        conflictResolutionStrategy: this.conflictResolutionStrategy
      }
      localStorage.setItem('syncState', JSON.stringify(syncState))
    } catch (error) {
      console.error('Failed to save sync state:', error)
    }
  }

  /**
   * Add item to sync queue
   */
  addToSyncQueue(item) {
    const existingIndex = this.syncQueue.findIndex(queueItem => 
      queueItem.type === item.type && queueItem.id === item.id
    )
    
    if (existingIndex !== -1) {
      // Update existing item with latest changes
      this.syncQueue[existingIndex] = { ...this.syncQueue[existingIndex], ...item }
    } else {
      this.syncQueue.push({
        ...item,
        timestamp: new Date().toISOString(),
        attempts: 0
      })
    }

    // Trigger sync if not already running
    if (!this.isSyncing) {
      this.processSync()
    }
  }

  /**
   * Sync local note to cloud
   */
  async syncNoteToCloud(note) {
    try {
      if (!this.cloudStorage.isConnected) {
        throw new Error('Not connected to cloud storage')
      }

      console.log('📤 Sync: Uploading note to cloud:', note.noteId)

      // Create note file content
      const noteContent = {
        noteId: note.noteId,
        title: note.title,
        content: note.content,
        folderId: note.folderId,
        isEncrypted: note.isEncrypted,
        createdAt: note.createdAt,
        lastModified: note.lastModified,
        attachments: note.attachments || []
      }

      const fileName = `note_${note.noteId}.json`
      const result = await this.cloudStorage.uploadFileContent(
        JSON.stringify(noteContent, null, 2),
        fileName,
        null // TODO: Use proper folder structure
      )

      if (result.success) {
        // Update local note with cloud file ID
        await localStorageService.updateNoteCloudId(note.noteId, result.data.id)
        console.log('✅ Sync: Note uploaded successfully:', result.data.id)
        return { success: true, cloudFileId: result.data.id }
      }

      throw new Error(result.error)

    } catch (error) {
      console.error('❌ Sync: Failed to upload note to cloud:', error)
      throw error
    }
  }

  /**
   * Sync cloud note to local
   */
  async syncNoteFromCloud(cloudFileId) {
    try {
      if (!this.cloudStorage.isConnected) {
        throw new Error('Not connected to cloud storage')
      }

      console.log('📥 Sync: Downloading note from cloud:', cloudFileId)

      const result = await this.cloudStorage.downloadFileContent(cloudFileId)
      
      if (result.success) {
        const noteData = JSON.parse(result.content)
        
        // Check if note exists locally
        const existingNote = await localStorageService.getNoteById(noteData.noteId)
        
        if (existingNote) {
          // Check for conflicts
          const conflict = this.detectConflict(existingNote, noteData)
          if (conflict) {
            this.syncConflicts.push(conflict)
            return { success: false, conflict }
          }
        }

        // Save/update note locally
        await localStorageService.saveNote(noteData, noteData.folderId)
        console.log('✅ Sync: Note downloaded and saved locally:', noteData.noteId)
        return { success: true, note: noteData }
      }

      throw new Error(result.error)

    } catch (error) {
      console.error('❌ Sync: Failed to download note from cloud:', error)
      throw error
    }
  }

  /**
   * Detect conflicts between local and cloud versions
   */
  detectConflict(localNote, cloudNote) {
    const localModified = new Date(localNote.lastModified)
    const cloudModified = new Date(cloudNote.lastModified)
    
    // If modification times are different and both have been modified since last sync
    if (Math.abs(localModified - cloudModified) > 1000) { // 1 second tolerance
      return {
        type: 'modification_conflict',
        noteId: localNote.noteId,
        localNote,
        cloudNote,
        localModified,
        cloudModified
      }
    }

    return null
  }

  /**
   * Resolve sync conflict
   */
  async resolveConflict(conflict, resolution) {
    try {
      let noteToKeep = null

      switch (resolution) {
        case 'keep_local':
          noteToKeep = conflict.localNote
          // Upload local version to cloud
          await this.syncNoteToCloud(conflict.localNote)
          break

        case 'keep_cloud':
          noteToKeep = conflict.cloudNote
          // Save cloud version locally
          await localStorageService.saveNote(conflict.cloudNote, conflict.cloudNote.folderId)
          break

        case 'merge':
          // Simple merge strategy - create new note with merged content
          noteToKeep = {
            ...conflict.localNote,
            title: `${conflict.localNote.title} (Merged)`,
            content: `${conflict.localNote.content}\n\n--- Merged from Cloud ---\n\n${conflict.cloudNote.content}`,
            lastModified: new Date().toISOString()
          }
          await localStorageService.saveNote(noteToKeep, noteToKeep.folderId)
          await this.syncNoteToCloud(noteToKeep)
          break

        case 'create_copy':
          // Keep both versions
          const copyNote = {
            ...conflict.cloudNote,
            noteId: `${conflict.cloudNote.noteId}_cloud_copy`,
            title: `${conflict.cloudNote.title} (Cloud Copy)`,
            lastModified: new Date().toISOString()
          }
          await localStorageService.saveNote(copyNote, copyNote.folderId)
          break
      }

      // Remove conflict from list
      this.syncConflicts = this.syncConflicts.filter(c => c.noteId !== conflict.noteId)
      
      console.log('✅ Sync: Conflict resolved:', resolution)
      toast.success('Sync conflict resolved successfully')

      return { success: true, resolvedNote: noteToKeep }

    } catch (error) {
      console.error('❌ Sync: Failed to resolve conflict:', error)
      toast.error('Failed to resolve sync conflict')
      throw error
    }
  }

  /**
   * Full sync - synchronize all local notes to cloud
   */
  async fullSync() {
    try {
      if (!this.cloudStorage.isConnected) {
        throw new Error('Not connected to cloud storage')
      }

      console.log('🔄 Sync: Starting full sync')
      this.isSyncing = true

      // Get all local notes
      const localNotes = await localStorageService.getAllNotes()
      
      // Get all cloud files
      const cloudResult = await this.cloudStorage.listFiles()
      if (!cloudResult.success) {
        throw new Error('Failed to list cloud files')
      }

      const cloudFiles = cloudResult.files.filter(file => 
        file.name.startsWith('note_') && file.name.endsWith('.json')
      )

      // Create sync plan
      const syncPlan = this.createSyncPlan(localNotes, cloudFiles)
      
      // Execute sync plan
      await this.executeSyncPlan(syncPlan)

      this.lastSyncTime = new Date()
      this.saveSyncState()

      console.log('✅ Sync: Full sync completed successfully')
      toast.success('All notes synchronized successfully')
      
      return { success: true, syncPlan }

    } catch (error) {
      console.error('❌ Sync: Full sync failed:', error)
      toast.error('Sync failed: ' + error.message)
      throw error
    } finally {
      this.isSyncing = false
    }
  }

  /**
   * Create sync plan comparing local and cloud states
   */
  createSyncPlan(localNotes, cloudFiles) {
    const plan = {
      upload: [], // Notes to upload to cloud
      download: [], // Notes to download from cloud
      conflicts: [], // Notes with conflicts
      delete: [] // Notes to delete (optional)
    }

    // Create maps for easier lookup
    const localNotesMap = new Map(localNotes.map(note => [note.noteId, note]))
    const cloudFilesMap = new Map(
      cloudFiles.map(file => {
        const noteId = file.name.replace('note_', '').replace('.json', '')
        return [noteId, file]
      })
    )

    // Check local notes
    for (const localNote of localNotes) {
      const cloudFile = cloudFilesMap.get(localNote.noteId)
      
      if (!cloudFile) {
        // Local note doesn't exist in cloud - upload it
        plan.upload.push(localNote)
      } else if (localNote.cloudFileId !== cloudFile.id) {
        // File exists but IDs don't match - potential conflict
        plan.conflicts.push({ local: localNote, cloud: cloudFile })
      }
    }

    // Check cloud files
    for (const [noteId, cloudFile] of cloudFilesMap) {
      const localNote = localNotesMap.get(noteId)
      
      if (!localNote) {
        // Cloud note doesn't exist locally - download it
        plan.download.push(cloudFile)
      }
    }

    return plan
  }

  /**
   * Execute sync plan
   */
  async executeSyncPlan(plan) {
    const results = {
      uploaded: 0,
      downloaded: 0,
      conflicts: 0,
      errors: []
    }

    // Upload local notes to cloud
    for (const note of plan.upload) {
      try {
        await this.syncNoteToCloud(note)
        results.uploaded++
      } catch (error) {
        results.errors.push({ type: 'upload', noteId: note.noteId, error: error.message })
      }
    }

    // Download cloud notes to local
    for (const cloudFile of plan.download) {
      try {
        await this.syncNoteFromCloud(cloudFile.id)
        results.downloaded++
      } catch (error) {
        results.errors.push({ type: 'download', fileId: cloudFile.id, error: error.message })
      }
    }

    // Handle conflicts based on strategy
    for (const conflict of plan.conflicts) {
      try {
        if (this.conflictResolutionStrategy === 'manual') {
          this.syncConflicts.push(conflict)
          results.conflicts++
        } else {
          await this.resolveConflict(conflict, this.conflictResolutionStrategy)
        }
      } catch (error) {
        results.errors.push({ type: 'conflict', noteId: conflict.local.noteId, error: error.message })
      }
    }

    return results
  }

  /**
   * Process sync queue
   */
  async processSync() {
    if (this.isSyncing || this.syncQueue.length === 0) {
      return
    }

    this.isSyncing = true

    try {
      while (this.syncQueue.length > 0) {
        const item = this.syncQueue.shift()
        
        try {
          switch (item.type) {
            case 'note':
              if (item.action === 'upload') {
                await this.syncNoteToCloud(item.data)
              } else if (item.action === 'download') {
                await this.syncNoteFromCloud(item.data.cloudFileId)
              }
              break

            // TODO: Add folder sync support
            case 'folder':
              console.log('Folder sync not yet implemented')
              break
          }
        } catch (error) {
          console.error('Sync queue item failed:', error)
          
          // Retry logic
          if (item.attempts < 3) {
            item.attempts++
            this.syncQueue.push(item)
          }
        }
      }
    } finally {
      this.isSyncing = false
    }
  }

  /**
   * Get sync status
   */
  getSyncStatus() {
    return {
      isConnected: this.cloudStorage?.isConnected || false,
      isSyncing: this.isSyncing,
      queueLength: this.syncQueue.length,
      conflictsCount: this.syncConflicts.length,
      lastSyncTime: this.lastSyncTime,
      provider: this.cloudStorage?.connectedProvider
    }
  }

  /**
   * Get pending conflicts
   */
  getConflicts() {
    return this.syncConflicts
  }

  /**
   * Set conflict resolution strategy
   */
  setConflictResolutionStrategy(strategy) {
    this.conflictResolutionStrategy = strategy
    this.saveSyncState()
  }
}

// Export singleton instance
export default new SyncService() 